import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import i18n from 'i18n-react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Diploma from 'components/Diploma';
import Modal from 'components/Modal';

/**
 * @typedef {object} DiplomaButtonProps
 * @property {string} firstName The first name by which the user is addressed on the diploma.
 * @property {string} lastName The lastName by which the user is addressed on the diploma
 * @property {string} email The email address of the user
 * @property {string} appId The appId of the app for which the diploma is generated
 * @property {number} diplomaRate The completion rate (0-100) of the diploma. Used to determine accessiblity of the diploma.
 * @property {string} [className] Additional classes to apply to the component. Used for styling outside of the component.
 * @property {Function} [onShow] Function to call when the modal is shown.
 * @property {Function} [onHide] Function to call when the modal is hidden.
 */

/**
 * @param {DiplomaButtonProps} props The props
 * @returns {JSX.Element} A button that opens a modal with the diploma when clicked.
 */
const DiplomaButton = ({
  firstName,
  lastName,
  email,
  appId,
  diplomaRate,
  className = '',
  onShow = () => {},
  onHide = () => {},
}) => {
  const [showDiploma, setShowDiploma] = useState(false);

  // Open the diploma modal if the diploma is unlocked
  const handleDiplomaOpen = useCallback(() => {
    setShowDiploma((prev) => {
      if (!prev && diplomaRate === 100) {
        return true;
      }
      return prev;
    });

    if (typeof onShow === 'function') {
      onShow();
    }
  }, [diplomaRate, onShow]);

  // Close the diploma modal
  const handleDiplomaClose = useCallback(() => {
    setShowDiploma((prev) => {
      if (prev) {
        return false;
      }
      return prev;
    });

    if (typeof onHide === 'function') {
      onHide();
    }
  }, [onHide]);

  const classes = clsx('diploma-button', className);

  const hasDiplomaDetails = useMemo(() => appId && firstName && lastName && email, [appId, email, firstName, lastName]);

  const renderedDiplomaLink = useMemo(() => {
    if (!hasDiplomaDetails) return null;
    if (diplomaRate === 100) {
      return (
        <button
          type="button"
          className="diploma-button__button diploma-button__button--active"
          onClick={handleDiplomaOpen}
        >
          <FontAwesomeIcon icon={['far', 'file-certificate']} />
          <p className="diploma-button__subtitle">{i18n.translate('nav.diploma')}</p>
        </button>
      );
    }

    return (
      <div className="diploma-button__button diploma-button__button--disabled">
        <FontAwesomeIcon icon={['far', 'file-certificate']} />
        <p className="diploma-button__subtitle">{i18n.translate('nav.diploma')}</p>
      </div>
    );
  }, [diplomaRate, handleDiplomaOpen, hasDiplomaDetails]);
  return (
    <div className={classes}>
      <Modal id="diploma-button__modal" show={showDiploma} onHide={handleDiplomaClose} bgClose autosize>
        <Diploma appId={appId} firstName={firstName} lastName={lastName} email={email} />
      </Modal>
      {renderedDiplomaLink}
    </div>
  );
};

DiplomaButton.propTypes = {
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  appId: PropTypes.number.isRequired,
  diplomaRate: PropTypes.number.isRequired,
  className: PropTypes.string,
  onShow: PropTypes.func,
  onHide: PropTypes.func,
};

export default DiplomaButton;
